
import { Component, Mixins } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import {
  Company,
  CompanyCollection,
  CompanyTypeEnum,
  IWeekOrders,
  Order,
  OrderCollection,
  OrderService
} from '@/firestore'
import store, { Actions } from '../../store'
import moment, { Moment } from 'moment'
import LoadingPage from '@/components/LoadingPage.vue'

interface PricingStat {
  restaurantId: string,
  restaurantDisplayName: string,
  weekOrdered: string,
  vendorOrderCount: number,
  totalOrderPrice: string,
  avgOrderPrice: string,
  isTotal?: number
}

@Component({
  components: {
    LoadingPage
  }
})  export default class AdminMetrics extends Mixins(IconsMixin, AuthMixin) {
  // defaults to monday-present of current week
  public dates: string[] = [moment().day(0).format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  public menu: boolean = false
  public loaded: boolean = false
  public tabs = null
  public orderService: OrderService | null = null

  public restaurants: Company[] = []
  public restaurantStats: {
    name: string,
    restaurantId: string,
    restaurantOrderCount: number,
    vendorOrderCount: number
  }[] = []

  public weekOrders: IWeekOrders[] = []
  public pricingStats: PricingStat[] = []

  public orderCountsHeaders = [
    {text: '', sortable: false, value: 'icon'},
    {text: 'Restaurant Name', value: 'name'},
    {text: 'Restaurant Order Count', value: 'restaurantOrderCount'},
    {text: 'Vendor Order Count', value: 'vendorOrderCount'}
  ]
  public pricingHeaders = [
    {text: '', sortable: false, value: 'icon'},
    {text: 'Week Ordered', value: 'weekOrdered'},
    {text: 'Restaurant Name', value: 'restaurantDisplayName'},
    {text: 'Vendor Order Count', value: 'vendorOrderCount'},
    {text: 'Total Order Price', value: 'totalOrderPrice'},
    {text: 'Avg Order Price', value: 'avgOrderPrice'}
  ]

  public get dateRangeText() {
    return this.dates.join(' ~ ')
  }

  public async mounted() {
    if (!this.userClaim || !this.userClaim.admin) {
      const err = new Error('You do not have permissions to view that page')
      store.dispatch(Actions.SET_ERROR, [err, this.$router])
    }

    this.orderService = new OrderService((await this.$firebase).firestore())

    const companyCollection = new CompanyCollection((await this.$firebase).firestore())
    const restaurants = await companyCollection.listCompaniesByType(CompanyTypeEnum.RESTAURANT)

    if (restaurants) {
      this.restaurants = restaurants
    }

    await this.setRestaurantStats()
    await this.setPricingStats()

    this.loaded = true
  }

  public convertToHeader(group: string) {
    return group
  }

  public weekOfMonth(input: Moment) {
    const firstDayOfMonth = input.clone().startOf('month')
    const firstDayOfWeek = firstDayOfMonth.clone().startOf('week')

    const offset = firstDayOfMonth.diff(firstDayOfWeek, 'days')

    return Math.ceil((input.date() + offset) / 7)
  }

  public updateStats() {
    if (!this.dates[0] || !this.dates[1]) {
      return
    }

    this.menu = false
    this.setRestaurantStats()
    this.setPricingStats()
  }

  public async setRestaurantStats() {
    const dates = this.getDateRange(this.dates)
    const beginDate = dates[0].toISOString()
    const endDate = dates[1].toISOString()

    const orderCollection = new OrderCollection((await this.$firebase).firestore())
    const orders: Order[] = await orderCollection.listByDateRange(beginDate, endDate)

    this.restaurants.map(restaurant => {
      const restaurantOrders: Order[] = orders.filter(order =>
          order.type === 'restaurant'
          && order.assignedCompanyId === restaurant.id
      )

      const vendorOrders: Order[] = orders.filter(order =>
          order.type === 'vendor'
          && order.orderedByCompanyId === restaurant.id
      )

      const restaurantOrderCount = restaurantOrders.length
      const vendorOrderCount = vendorOrders.length
      const matchingRestaurant = this.restaurantStats.find(r => r.restaurantId === restaurant.id)
      if (matchingRestaurant) {
        matchingRestaurant.restaurantOrderCount = restaurantOrderCount
        matchingRestaurant.vendorOrderCount = vendorOrderCount
      } else {
        this.restaurantStats.push({
          name: restaurant.displayName,
          restaurantId: restaurant.id,
          restaurantOrderCount,
          vendorOrderCount
        })
      }
    })
  }

  public async setPricingStats() {
    this.pricingStats = []
    const dates = this.getDateRange(this.dates)
    const beginDate = dates[0].toDate()
    const endDate = dates[1].toDate()

    if (!this.orderService) {
      return
    }

    this.weekOrders = await this.orderService.getOrderVolume(beginDate, endDate)

    if (!this.weekOrders || !this.weekOrders.length) {
      return
    }

    const totalAcrossAllRestaurants = await this.orderService.getTotalOrderVolume(this.weekOrders)

    this.weekOrders.push(...totalAcrossAllRestaurants)

    const unsortedPricingStats = this.weekOrders.reduce((acc, weekOrder) => {
      const pricingStat = {
        restaurantId: weekOrder.restaurantId,
        restaurantDisplayName:
            weekOrder.restaurantIdentifier
                ? `${weekOrder.restaurantName} (${weekOrder.restaurantIdentifier})`
                : weekOrder.restaurantName,
        weekOrdered: moment(weekOrder.weekOrdered).format('MM/DD/YYYY'),
        vendorOrderCount: weekOrder.ordersCount,
        totalOrderPrice: `$${weekOrder.ordersTotalPrice.toFixed(2)}`,
        avgOrderPrice: `$${weekOrder.ordersAveragePrice.toFixed(2)}`,
        isTotal: weekOrder.restaurantId === '1' ? 1 : 0
      } as PricingStat
      return [...acc, pricingStat]
    }, [] as PricingStat[])

    this.pricingStats = unsortedPricingStats.sort((a: PricingStat, b: PricingStat) => {
      if (moment(a.weekOrdered) > moment(b.weekOrdered)) {
        return 1
      } else {
        return -1
      }
    })
  }

  private getDateRange(dates: string[]) {
    const firstDate = moment(this.dates[0], 'YYYY-MM-DD')
    const secondDate = moment(this.dates[1], 'YYYY-MM-DD')

    if (firstDate.isBefore(secondDate)) {
      return [firstDate, secondDate.endOf('day')]
    } else {
      return [secondDate, firstDate.endOf('day')]
    }
  }
}
